import { TFunction } from "i18next";
import camelCase from "lodash/camelCase";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import startCase from "lodash/startCase";
import i18n from "../../../i18n";
import { BaseActivity } from "../../../model/activity/BaseActivity";
import { Entry } from "../../../model/entry/Entry";
import { ActivityAction } from "../../../model/activity/types/ActivityAction.enum";
import { ActivityEntityKind } from "../../../model/activity/types/ActivityEntityKind.enum";
import {
  ACTIVITY_META_MODIFIER_KEYS,
  ActivityMetaKeys,
} from "../../../model/activity/types/ActivityMetaKeys.enum";
import { ThemeType } from "../../../theme/Theme";
import { faPen } from "@fortawesome/free-solid-svg-icons/faPen";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faArrowRotateRight } from "@fortawesome/free-solid-svg-icons/faArrowRotateRight";
import { faExchangeAlt } from "@fortawesome/free-solid-svg-icons/faExchangeAlt";
import { faFileImport } from "@fortawesome/free-solid-svg-icons/faFileImport";
import { faUserMinus } from "@fortawesome/free-solid-svg-icons/faUserMinus";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons/faUserPlus";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faFileExport } from "@fortawesome/free-solid-svg-icons/faFileExport";
import { faAngleUp } from "@fortawesome/free-solid-svg-icons/faAngleUp";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons/faCircleInfo";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons/faPaperclip";
import { faLinkSlash } from "@fortawesome/free-solid-svg-icons/faLinkSlash";
import { faCodeBranch } from "@fortawesome/free-solid-svg-icons/faCodeBranch";
import isArray from "lodash/isArray";
import isBoolean from "lodash/isBoolean";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import { isValidDate, standardDate } from "../../../utils/Date";
import { User } from "../../../model/user/User";
import { KanbanColumnAutomationActionType } from "../../../model/kanban/types/KanbanColumnAutomationActionType.enum";
import { kanbanAutomationActionMeta } from "../../../routes/kanban/KanbanAutomationUtils";

export const ActivityDotColor: Partial<
  Record<ActivityAction, keyof ThemeType>
> = {
  [ActivityAction.CREATE]: "main",
  [ActivityAction.READ]: "darkerSub",
  [ActivityAction.UPDATE]: "main",
  [ActivityAction.DELETE]: "danger",
  [ActivityAction.ASSIGN]: "main",
  [ActivityAction.UNASSIGN]: "danger",
  [ActivityAction.CONVERT]: "main",
  [ActivityAction.COMPLETE]: "success",
  [ActivityAction.EXPORT]: "main",
  [ActivityAction.IMPORT]: "main",
  [ActivityAction.INCREMENT_ATTEMPT]: "success",
  [ActivityAction.MARK_FOR_DELETION]: "warning",
  [ActivityAction.MARK_FOR_DELETION_UNDO]: "main",
  [ActivityAction.MERGE]: "main",
  [ActivityAction.ATTACH]: "main",
  [ActivityAction.DETACH]: "warning",
  [ActivityAction.MOVE_BETWEEN_KANBANS]: "main",
};

export const ActivityToIcon: Partial<Record<ActivityAction, IconDefinition>> = {
  [ActivityAction.CREATE]: faPlus,
  [ActivityAction.READ]: faEye,
  [ActivityAction.UPDATE]: faPen,
  [ActivityAction.DELETE]: faTrash,
  [ActivityAction.ASSIGN]: faUserPlus,
  [ActivityAction.UNASSIGN]: faUserMinus,
  [ActivityAction.CONVERT]: faExchangeAlt,
  [ActivityAction.COMPLETE]: faCheck,
  [ActivityAction.EXPORT]: faFileExport,
  [ActivityAction.IMPORT]: faFileImport,
  [ActivityAction.INCREMENT_ATTEMPT]: faAngleUp,
  [ActivityAction.MARK_FOR_DELETION]: faTrash,
  [ActivityAction.MARK_FOR_DELETION_UNDO]: faArrowRotateRight,
  [ActivityAction.ATTACH]: faPaperclip,
  [ActivityAction.DETACH]: faLinkSlash,
  [ActivityAction.MOVE_BETWEEN_KANBANS]: faExchangeAlt,
  [ActivityAction.MERGE]: faCodeBranch,
};

/**
 * The API is free to record an action this build has never heard of. Falling
 * through to `undefined` here paints a dot with no background and hands
 * FontAwesome an undefined icon, so always resolve to something drawable.
 */
export const getActivityDotColor = (action: ActivityAction): keyof ThemeType =>
  ActivityDotColor[action] || "darkerSub";

export const getActivityIcon = (action: ActivityAction): IconDefinition =>
  ActivityToIcon[action] || faCircleInfo;

/**
 * Meta keys that describe the change better than the bare action does - either
 * because the action is the vague UPDATE (the legacy card-reorder records) or
 * because ATTACH alone does not say what was attached.
 *
 * Actions that are already specific, MOVE_BETWEEN_KANBANS among them, are left
 * to their own label.
 */
const META_KEY_LABEL_KEYS: Partial<Record<string, string>> = {
  [ActivityMetaKeys.CHANGE_COLUMN]: "activity.actions.type.changeColumn",
  [ActivityMetaKeys.MOVE]: "activity.actions.type.move",
  [ActivityMetaKeys.ATTACH_TASK]: "activity.actions.type.attachTask",
  [ActivityMetaKeys.ATTACH_MODULE]: "activity.actions.type.attachModule",
  [ActivityMetaKeys.DETACH_MODULE]: "activity.actions.type.detachModule",
  [ActivityMetaKeys.ATTACH_KANBAN_CARD]: "activity.actions.type.attachDeal",
  [ActivityMetaKeys.TAGS]: "activity.actions.type.tags",
};

/**
 * Meta keys whose whole payload is a boolean restating the action. Opening a
 * "View Details" panel on one of these shows `Value: true`.
 */
const VALUELESS_META_KEYS: string[] = [
  ActivityMetaKeys.MARK_DELETE,
  ActivityMetaKeys.MARK_DELETE_UNDO,
  ActivityMetaKeys.EXPORT,
  ActivityMetaKeys.IMPORT,
];

/** Meta keys carrying an actual payload, in the order the API wrote them. */
export const getActivityMetaKeys = (activity: BaseActivity): string[] =>
  Object.keys(activity.meta || {}).filter(
    (key) => !ACTIVITY_META_MODIFIER_KEYS.includes(key),
  );

/**
 * "Nothing to show" for a single value.
 *
 * Deliberately not lodash's `isEmpty`, which reports every non-collection
 * primitive as empty: `isEmpty(0)`, `isEmpty(false)` and `isEmpty(new Date())`
 * are all `true`. Using it here would suppress `order: 3 -> 5`,
 * `done: false -> true` and every date change as if nothing had happened.
 */
export const isBlankValue = (v: any) =>
  isNil(v) || v === "" || (isArray(v) && !v.length);

/** A `{ from, to }` pair with nothing on either side records no visible change. */
export const isBlankDiff = (value: any) =>
  isObject(value) &&
  ("from" in value || "to" in value) &&
  isBlankValue((value as any).from) &&
  isBlankValue((value as any).to);

/**
 * A PATCH made entirely of blank diffs renders nothing, so it must not be the
 * reason a "View Details" toggle appears. Older activities carry these because
 * the recorder used to treat `undefined -> null` as an edit.
 */
const hasRenderableContent = (value: any): boolean => {
  if (isEmpty(value)) {
    return false;
  }

  if (!isObject(value) || isBlankDiff(value)) {
    return !isBlankDiff(value);
  }

  const entries = Object.entries(value as Record<string, any>);

  return entries.length ? entries.some(([, v]) => !isBlankDiff(v)) : false;
};

/** The meta keys the "View Details" panel should actually render. */
export const getActivityDetailKeys = (activity: BaseActivity): string[] =>
  getActivityMetaKeys(activity).filter(
    (key) =>
      !VALUELESS_META_KEYS.includes(key) &&
      hasRenderableContent(activity.meta?.[key as keyof typeof activity.meta]),
  );

/** Whether the collapse has anything to put inside it. */
export const hasActivityDetails = (activity: BaseActivity): boolean =>
  getActivityDetailKeys(activity).length > 0;

/** A column automation rule's change is recorded on its column, and told
 * apart from the column's own changes by the rule it names. */
export const isAutomationRuleActivity = (activity: BaseActivity) =>
  activity.kind === ActivityEntityKind.KANBAN_COLUMN &&
  Boolean(activity.meta?.shared?.automationRuleId);

export const AUTOMATION_RULE_FIELD_LABEL_KEYS: Record<string, string> = {
  column: "common.column",
  actionType: "kanban.automation.rule.form.actionType.label",
  enabled: "kanban.automation.rule.form.enabled.label",
  repeatable: "kanban.automation.activity.repeatable",
  recipients: "kanban.automation.rule.form.recipients.title",
  assignees: "kanban.automation.rule.form.assignees.title",
  taskName: "kanban.automation.rule.form.taskName.label",
  taskDescription: "kanban.automation.rule.form.taskDescription.label",
};

export const translateAutomationActionType = (
  t: TFunction,
  actionType: unknown,
): string => {
  const meta =
    kanbanAutomationActionMeta[actionType as KanbanColumnAutomationActionType];

  return meta ? t(meta.titleKey) : String(actionType ?? "");
};

/** What the activity happened *to*, e.g. "Deal", "Lead Task", "Module Instance". */
export const getActivityEntityLabel = (
  activity: BaseActivity,
  t: TFunction,
): string => {
  const { taskKind, moduleKind } = activity.meta?.shared || {};

  if (activity.kind === ActivityEntityKind.TASK && taskKind) {
    return t(`common.${camelCase(taskKind)}_task`);
  }

  if (isAutomationRuleActivity(activity)) {
    return t("kanban.automation.activity.entity");
  }

  if (activity.kind === ActivityEntityKind.MODULE) {
    return moduleKind === "MODULE_DEFINITION"
      ? t("common.moduleDefinition")
      : t("common.moduleInstance");
  }

  return t(`common.${camelCase(activity.kind)}`);
};

/**
 * The label for an action on its own, e.g. "Updated", "Member unassigned".
 *
 * Reports group by action and have no activity object to hand, so this is the
 * one place that turns an action into words - previously reports built their own
 * `common.<action>` key, which did not exist for six of the sixteen actions.
 */
export const translateActivityAction = (
  t: TFunction,
  action?: ActivityAction | string,
): string => {
  if (!action) {
    return t("activity.actions.unknown");
  }

  // `defaultValue` covers an action recorded by a newer API than this build knows.
  return t(`activity.actions.${camelCase(action)}.done`, {
    defaultValue: t("activity.actions.unknown"),
  });
};

/** What was done, e.g. "Updated", "Moved between columns", "Member unassigned". */
export const getActivityActionLabel = (
  activity: BaseActivity,
  t: TFunction,
): string => {
  const metaLabelKey = getActivityMetaKeys(activity)
    .map((key) => META_KEY_LABEL_KEYS[key])
    .find(Boolean);

  if (metaLabelKey) {
    return t(metaLabelKey);
  }

  return translateActivityAction(t, activity.action);
};

/**
 * The single line shown on a timeline card, e.g. "Deal - Change pipeline".
 *
 * This deliberately reads the *action* rather than the first meta key: meta keys
 * are an implementation detail of the recorder, several actions carry none at
 * all, and object key order decided which one won.
 */
export const getActivityHeadline = (
  activity: BaseActivity,
  t: TFunction,
): string => {
  const headline = `${getActivityEntityLabel(activity, t)} - ${getActivityActionLabel(activity, t)}`;

  return activity.meta?.[ActivityMetaKeys.IS_BULK]?.value
    ? `${headline} (${t("activity.actions.type.isBulk")})`
    : headline;
};

/**
 * Field names in a diff are open-ended: entry fields, form inputs and module
 * inputs all end up here, so `common.<key>` cannot be guaranteed to exist.
 * Fall back to the humanised key rather than printing the lookup itself.
 */
export const translateFieldLabel = (t: TFunction, field: string): string =>
  t(`common.${camelCase(field)}`, { defaultValue: humanizeFieldName(field) });

/** `markedForDeletionAt` -> `Marked For Deletion At`, `custom-field-1` -> `Custom Field 1`. */
export const humanizeFieldName = (field: string): string =>
  startCase(camelCase(field));

/** The form input behind an entry activity's field, when that entry is the one open in the drawer. */
export const getEntryActivityInput = (
  activity: BaseActivity,
  entry: Entry | null,
  field: string,
) =>
  activity.kind === ActivityEntityKind.ENTRY &&
  activity.resourceId === entry?._id
    ? entry?.parentForm?.inputs?.find((input) => input.name === field)
    : undefined;

export const formatDisplayValue = (v: any, user: User): string => {
  if (isNil(v) || v === "") {
    return "";
  }

  if (isBoolean(v)) {
    return v ? i18n.t("common.yes") : i18n.t("common.no");
  }

  // Lists read as lists. `JSON.stringify` turned a form's domains into
  // `["a.com","b.com"]`, quotes and brackets included.
  if (isArray(v)) {
    return v
      .map((item) => formatDisplayValue(item, user))
      .filter(Boolean)
      .join(", ");
  }

  if (isObject(v)) {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }

  if (isValidDate(v)) {
    const date = new Date(v);
    return standardDate(date, user, true);
  }

  return String(v);
};
